import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import OrderTimeline from './OrderTimeline';

const OrdersList = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending_payment, paid, shipping, delivered
  const [orderTypeFilter, setOrderTypeFilter] = useState('all'); // all, buy, sell
  const [expandedTimelines, setExpandedTimelines] = useState({}); // Track which timelines are expanded

  useEffect(() => {
    if (currentUser) {
      fetchOrders();
    }
  }, [currentUser, filter, orderTypeFilter]);

  const fetchOrders = async () => {
    if (!currentUser) {
      console.log('No current user, skipping fetch');
      setLoading(false);
      return;
    }

    setLoading(true);
    console.log('🔍 Starting to fetch orders...');
    console.log(`Current user UID: ${currentUser.uid}`);
    
    try {
      // ใช้ fallback ตั้งแต่ต้น: ดึงทั้งหมดแล้วกรองใน client
      // เพื่อหลีกเลี่ยงปัญหา index
      console.log('📦 Fetching all orders from database...');
      const allOrdersSnapshot = await getDocs(collection(db, 'orders'));
      console.log(`✅ Total orders in database: ${allOrdersSnapshot.docs.length}`);
      
      const allOrders = allOrdersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          isBuyer: data.buyerId === currentUser.uid
        };
      });

      // แสดงตัวอย่างออเดอร์ (3 รายการแรก)
      if (allOrders.length > 0) {
        console.log('📋 Sample orders:', allOrders.slice(0, 3).map(o => ({
          id: o.id,
          orderNumber: o.orderNumber,
          buyerId: o.buyerId,
          sellerId: o.sellerId,
          status: o.status,
          matchesBuyer: o.buyerId === currentUser.uid,
          matchesSeller: o.sellerId === currentUser.uid
        })));
      }

      // กรองเฉพาะออเดอร์ที่เกี่ยวข้องกับผู้ใช้
      const buyerOrders = allOrders
        .filter(order => {
          const matches = order.buyerId === currentUser.uid;
          if (matches) {
            console.log(`✅ Found buyer order: ${order.orderNumber || order.id}`);
          }
          return matches;
        })
        .map(order => ({ ...order, isBuyer: true }));
      
      const sellerOrders = allOrders
        .filter(order => {
          const matches = order.sellerId === currentUser.uid;
          if (matches) {
            console.log(`✅ Found seller order: ${order.orderNumber || order.id}`);
          }
          return matches;
        })
        .map(order => ({ ...order, isBuyer: false }));
      
      console.log(`📊 Buyer orders: ${buyerOrders.length}, Seller orders: ${sellerOrders.length}`);

      // รวมออเดอร์ทั้งสองฝั่ง
      const combinedOrders = [...buyerOrders, ...sellerOrders];

      // ลบออเดอร์ซ้ำ (ถ้ามี) และเรียงตามวันที่
      const uniqueOrders = combinedOrders
        .filter((order, index, self) => 
          index === self.findIndex(o => o.id === order.id)
        )
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return dateB - dateA;
        });

      console.log(`📦 Unique orders after deduplication: ${uniqueOrders.length}`);

      // กรองตามประเภทออเดอร์ (ซื้อ/ขาย)
      let typeFilteredOrders = uniqueOrders;
      if (orderTypeFilter === 'buy') {
        typeFilteredOrders = uniqueOrders.filter(order => order.isBuyer === true);
        console.log(`🛒 Buyer orders after type filter: ${typeFilteredOrders.length}`);
      } else if (orderTypeFilter === 'sell') {
        typeFilteredOrders = uniqueOrders.filter(order => order.isBuyer === false);
        console.log(`🏪 Seller orders after type filter: ${typeFilteredOrders.length}`);
      }

      // กรองตามสถานะ
      let filteredOrders = typeFilteredOrders;
      if (filter !== 'all') {
        filteredOrders = typeFilteredOrders.filter(order => {
          const matches = order.status === filter;
          if (matches) {
            console.log(`✅ Order ${order.orderNumber || order.id} matches filter ${filter}`);
          }
          return matches;
        });
        console.log(`🔍 Orders after status filter (${filter}): ${filteredOrders.length}`);
      }

      console.log(`✅ Final orders to display: ${filteredOrders.length}`);
      setOrders(filteredOrders);
    } catch (error) {
      console.error('❌ Error fetching orders:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      setOrders([]);
    } finally {
      setLoading(false);
      console.log('🏁 Finished fetching orders');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const toggleTimeline = (orderId) => {
    setExpandedTimelines(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-4 md:py-8">
      <div className="container mx-auto px-3 md:px-4">
        {/* Header Section */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-green-600 to-green-800 bg-clip-text text-transparent mb-2">
            {t('orders.title')}
          </h1>
          <p className="text-sm md:text-base text-gray-500">
            {orders.length > 0 ? `${orders.length} ${t('orders.myOrders')}` : t('orders.noOrders')}
          </p>
        </div>

        {/* Filters Section - Enhanced Design */}
        <div className="mb-6 md:mb-8 space-y-4">
          {/* Order Type Filter */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">
              {t('orders.filterByType') || 'ประเภทออเดอร์'}
            </label>
            <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-3 px-3 md:mx-0 md:px-0 pb-2">
              <button
                onClick={() => setOrderTypeFilter('all')}
                className={`px-4 md:px-5 py-2.5 rounded-xl font-semibold text-sm md:text-base whitespace-nowrap flex-shrink-0 transition-all duration-200 transform hover:scale-105 ${
                  orderTypeFilter === 'all' 
                    ? 'bg-gradient-to-r from-gray-700 to-gray-800 text-white shadow-lg shadow-gray-300' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200 hover:border-gray-300 shadow-sm'
                }`}
              >
                <span className="mr-1.5">📋</span>
                {t('orders.all')}
              </button>
              <button
                onClick={() => setOrderTypeFilter('buy')}
                className={`px-4 md:px-5 py-2.5 rounded-xl font-semibold text-sm md:text-base whitespace-nowrap flex-shrink-0 transition-all duration-200 transform hover:scale-105 ${
                  orderTypeFilter === 'buy' 
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-300' 
                    : 'bg-white text-gray-700 hover:bg-blue-50 border-2 border-gray-200 hover:border-blue-300 shadow-sm'
                }`}
              >
                <span className="mr-1.5">🛒</span>
                {t('orders.buyOrders')}
              </button>
              <button
                onClick={() => setOrderTypeFilter('sell')}
                className={`px-4 md:px-5 py-2.5 rounded-xl font-semibold text-sm md:text-base whitespace-nowrap flex-shrink-0 transition-all duration-200 transform hover:scale-105 ${
                  orderTypeFilter === 'sell' 
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-300' 
                    : 'bg-white text-gray-700 hover:bg-green-50 border-2 border-gray-200 hover:border-green-300 shadow-sm'
                }`}
              >
                <span className="mr-1.5">🏪</span>
                {t('orders.sellOrders')}
              </button>
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-1">
              {t('orders.filterByStatus') || 'สถานะ'}
            </label>
            <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-3 px-3 md:mx-0 md:px-0 pb-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 md:px-5 py-2.5 rounded-xl font-semibold text-sm md:text-base whitespace-nowrap flex-shrink-0 transition-all duration-200 transform hover:scale-105 ${
                  filter === 'all' 
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-300' 
                    : 'bg-white text-gray-700 hover:bg-green-50 border-2 border-gray-200 hover:border-green-300 shadow-sm'
                }`}
              >
                {t('orders.allStatus')}
              </button>
              <button
                onClick={() => setFilter('pending_payment')}
                className={`px-4 md:px-5 py-2.5 rounded-xl font-semibold text-sm md:text-base whitespace-nowrap flex-shrink-0 transition-all duration-200 transform hover:scale-105 ${
                  filter === 'pending_payment' 
                    ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-lg shadow-yellow-300' 
                    : 'bg-white text-gray-700 hover:bg-yellow-50 border-2 border-gray-200 hover:border-yellow-300 shadow-sm'
                }`}
              >
                ⏳ {t('orders.pendingPayment')}
              </button>
              <button
                onClick={() => setFilter('paid')}
                className={`px-4 md:px-5 py-2.5 rounded-xl font-semibold text-sm md:text-base whitespace-nowrap flex-shrink-0 transition-all duration-200 transform hover:scale-105 ${
                  filter === 'paid' 
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-300' 
                    : 'bg-white text-gray-700 hover:bg-blue-50 border-2 border-gray-200 hover:border-blue-300 shadow-sm'
                }`}
              >
                💳 {t('orders.paid')}
              </button>
              <button
                onClick={() => setFilter('shipping')}
                className={`px-4 md:px-5 py-2.5 rounded-xl font-semibold text-sm md:text-base whitespace-nowrap flex-shrink-0 transition-all duration-200 transform hover:scale-105 ${
                  filter === 'shipping' 
                    ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg shadow-purple-300' 
                    : 'bg-white text-gray-700 hover:bg-purple-50 border-2 border-gray-200 hover:border-purple-300 shadow-sm'
                }`}
              >
                🚚 {t('orders.shipping')}
              </button>
              <button
                onClick={() => setFilter('delivered')}
                className={`px-4 md:px-5 py-2.5 rounded-xl font-semibold text-sm md:text-base whitespace-nowrap flex-shrink-0 transition-all duration-200 transform hover:scale-105 ${
                  filter === 'delivered' 
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg shadow-green-300' 
                    : 'bg-white text-gray-700 hover:bg-green-50 border-2 border-gray-200 hover:border-green-300 shadow-sm'
                }`}
              >
                ✅ {t('orders.delivered')}
              </button>
            </div>
          </div>
        </div>

        {orders.length > 0 ? (
          <div className="space-y-4 md:space-y-5">
            {orders.map(order => (
              <div
                key={order.id}
                className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-gray-200 transform hover:-translate-y-1"
              >
                {/* Order Header with Gradient */}
                <div 
                  onClick={() => navigate(`/marketplace/orders/${order.id}`)}
                  className="cursor-pointer"
                >
                  <div className={`p-5 md:p-6 ${
                    order.isBuyer 
                      ? 'bg-gradient-to-r from-blue-50 via-blue-50 to-white' 
                      : 'bg-gradient-to-r from-green-50 via-green-50 to-white'
                  }`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1 min-w-0">
                        {/* Order ID and Type Badge */}
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <h3 className="text-lg md:text-xl font-bold text-gray-900">
                            {t('orders.orderId')} <span className="text-gray-600">#{order.orderNumber}</span>
                          </h3>
                          {/* Enhanced Order Type Badge */}
                          <span className={`px-3 py-1.5 rounded-full text-xs font-bold shadow-sm ${
                            order.isBuyer 
                              ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' 
                              : 'bg-gradient-to-r from-green-500 to-green-600 text-white'
                          }`}>
                            {order.isBuyer ? '🛒 ' + t('orders.buyOrder') : '🏪 ' + t('orders.sellOrder')}
                          </span>
                        </div>
                        
                        {/* Order Info Cards */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <span className="text-gray-400">👤</span>
                            <span className="font-medium">{order.isBuyer ? t('orders.seller') : t('orders.buyer')}:</span>
                            <span className="font-semibold text-gray-900">{order.isBuyer ? order.sellerName : order.buyerName}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <span className="text-gray-400">🏪</span>
                            <span className="font-medium">{t('orders.store')}:</span>
                            <span className="font-semibold text-gray-900">{order.storeName}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <span className="text-gray-400">📅</span>
                            <span className="font-medium">{t('orders.date')}:</span>
                            <span className="text-gray-900">{new Date(order.createdAt?.toDate?.() || order.createdAt).toLocaleDateString('th-TH')}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Right Side - Status and Amount */}
                      <div className="text-right ml-4 flex-shrink-0">
                        {/* Enhanced Status Badge */}
                        <div className={`px-3 py-1.5 rounded-xl text-xs md:text-sm font-bold mb-3 shadow-sm ${
                          order.status === 'pending_payment' ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white' :
                          order.status === 'paid' ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white' :
                          order.status === 'shipping' ? 'bg-gradient-to-r from-purple-400 to-purple-500 text-white' :
                          order.status === 'delivered' ? 'bg-gradient-to-r from-green-400 to-green-500 text-white' :
                          'bg-gradient-to-r from-gray-400 to-gray-500 text-white'
                        }`}>
                          {order.status === 'pending_payment' ? '⏳ ' + t('orders.pendingPayment') :
                           order.status === 'paid' ? '💳 ' + t('orders.paid') :
                           order.status === 'shipping' ? '🚚 ' + t('orders.shipping') :
                           order.status === 'delivered' ? '✅ ' + t('orders.delivered') :
                           order.status}
                        </div>
                        {/* Enhanced Amount Display */}
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3 border border-green-200 mb-3">
                          <p className="text-xs text-gray-600 mb-1 font-medium">{t('orders.total')}</p>
                          <p className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
                            ฿{order.total?.toLocaleString()}
                          </p>
                        </div>
                        {/* Product Image with Name Overlay - Below Price */}
                        {order.items && order.items.length > 0 && order.items[0].image && (
                          <div className="flex flex-col items-end">
                            <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 border-gray-200 shadow-sm">
                              <div className="relative w-full h-full">
                                <img
                                  src={order.items[0].image}
                                  alt={order.items[0].productName || order.items[0].name || 'Product'}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                                {/* Gradient overlay for text readability */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                                {/* Subtle border overlay */}
                                <div className="absolute inset-0 border border-gray-200/30 rounded-lg"></div>
                              </div>
                              {/* Product Name Overlay */}
                              <div className="absolute bottom-0 left-0 right-0 p-1.5 md:p-2">
                                <p className="text-[10px] md:text-xs text-white font-semibold text-center line-clamp-2 leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                                  {order.items[0].productName || order.items[0].name || 'สินค้า'}
                                </p>
                              </div>
                              {/* Show count if multiple items */}
                              {order.items.length > 1 && (
                                <div className="absolute -top-1 -right-1 bg-gray-800/80 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center border border-white z-10">
                                  +{order.items.length - 1}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Timeline Section */}
                <div className="px-5 md:px-6 pb-5 md:pb-6">
                  <OrderTimeline 
                    order={order} 
                    isExpanded={expandedTimelines[order.id] || false}
                    onToggle={() => toggleTimeline(order.id)}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-12 md:p-16 text-center border border-gray-100">
            <div className="max-w-md mx-auto">
              <div className="text-6xl mb-4">📦</div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">{t('orders.noOrders')}</h3>
              <p className="text-sm md:text-base text-gray-500">
                {t('orders.noOrdersDescription') || 'ยังไม่มีออเดอร์ในระบบ'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersList;



